import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWorkspaceOptional } from '@/contexts/WorkspaceContext';
import { can } from '@/lib/permissions';
import { describeApiError, type ApiErrorAction } from '@/lib/apiError';

// The rendering half of `describeApiError`, kept separate so that function stays pure and testable.
//
// It is also the half that knows two things the pure function must not: the workspace route, and
// whether this user can even reach /billing. That permission check is not cosmetic — /billing sits
// behind `RequireRole permission="billing:read"`, which `<Navigate>`s away, so a "View plans" button
// shown to a content or media role would land them on a silent redirect.

// A toast with a button has to outlast the reading of it; four seconds is a tease.
const DURATION_WITH_ACTION = 10000;

export function useApiErrorToast() {
  const navigate = useNavigate();
  const workspace = useWorkspaceOptional();
  const workspaceId = workspace?.workspaceId;
  const canManageBilling = can(workspace?.permissions, 'billing:read');

  const runAction = useCallback((action: ApiErrorAction) => {
    if (action.kind === 'billing' && workspaceId) navigate(`/w/${workspaceId}/billing`);
    else if (action.kind === 'signin') navigate('/login');
  }, [navigate, workspaceId]);

  /**
   * Report a failed request. `fallback` is what this action was trying to do, in the page's own
   * words — used only when the server gave us nothing better.
   */
  const showError = useCallback((error: unknown, fallback: string) => {
    const { title, description, action } = describeApiError(error, { fallback, canManageBilling });

    // A billing action with no workspace in context has nowhere to go, so it is dropped rather
    // than rendered as a button that does nothing.
    const usable = action && (action.kind !== 'billing' || workspaceId) ? action : undefined;

    toast.error(title, {
      description,
      ...(usable
        ? {
          duration: DURATION_WITH_ACTION,
          action: { label: usable.label, onClick: () => runAction(usable) },
        }
        : {}),
    });
  }, [canManageBilling, runAction, workspaceId]);

  return showError;
}

export default useApiErrorToast;
